import { useEffect, useState } from "react";
import API from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ArrowsClockwise, Plugs, BookOpenText } from "@phosphor-icons/react";
import { CardShell, AppNotConfigured, fmtWhen } from "./shared";

const PICKS = [
  { key: "deposit_account", label: "Deposit account for payments", list: "deposit_accounts", hint: "Where card / bank / Direct Debit payments are recorded" },
  { key: "tax_code", label: "VAT tax code", list: "tax_codes", hint: "Applied to every line when VAT is on (e.g. 20.0% S)" },
  { key: "item", label: "Service item", list: "items", hint: "Defaults to a \"Fixlr Services\" item we create for you" },
];

export default function QuickBooksCard({ item, isAdmin, onChange }) {
  const conn = item?.connection;
  const [busy, setBusy] = useState("");
  const [ref, setRef] = useState(null);
  const [settings, setSettings] = useState(conn?.settings || {});
  const [open, setOpen] = useState(false);
  useEffect(() => { setSettings(conn?.settings || {}); }, [conn]);

  const connect = async () => {
    setBusy("connect");
    try {
      const { data } = await API.post("/integrations/quickbooks/connect", { back: "/settings?tab=integrations" });
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start QuickBooks sign-in"); setBusy(""); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect QuickBooks? Existing links are kept; nothing new will sync.")) return;
    setBusy("disconnect");
    try { await API.post("/integrations/quickbooks/disconnect"); toast.success("QuickBooks disconnected"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Disconnect failed"); }
    finally { setBusy(""); }
  };
  const loadRef = async () => {
    if (ref) return;
    try { const { data } = await API.get("/integrations/quickbooks/reference"); setRef(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load QuickBooks accounts"); setRef({}); }
  };
  const save = async (patch) => {
    setSettings((s) => ({ ...s, ...patch }));
    try { await API.put("/integrations/quickbooks/settings", patch); toast.success("QuickBooks settings saved"); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const sync = async () => {
    setBusy("sync");
    try { const { data } = await API.post("/integrations/quickbooks/sync"); toast.success(`QuickBooks checked · ${data.marked_paid} invoice(s) marked paid`); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try { const { data } = await API.post("/integrations/quickbooks/test"); toast.success(`Connected to ${data.name || "QuickBooks"} (${data.environment})`); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Connection test failed"); onChange(); }
    finally { setBusy(""); }
  };

  const options = (listKey, curId, curName) => {
    const list = ref?.[listKey]?.length ? ref[listKey] : (curId ? [{ id: curId, name: curName || curId }] : []);
    return list.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}{a.code ? ` (${a.code})` : ""}</SelectItem>);
  };

  return (
    <CardShell icon={<BookOpenText size={18} weight="duotone" className="text-green-600" />} title="QuickBooks Online" item={item} testid="integration-quickbooks">
      {conn?.status && conn.status !== "disconnected" ? (
        <div className="space-y-3">
          <div className="text-xs space-y-0.5">
            <p><span className="text-muted-foreground">Company:</span> <b data-testid="quickbooks-company-name">{conn.account?.name || conn.account?.realm_id}</b> <span className="uppercase text-[10px] text-muted-foreground" data-testid="quickbooks-env">{conn.account?.environment}</span></p>
            <p className="text-muted-foreground">Last sync {fmtWhen(conn.last_sync_at)}{conn.last_error ? <span className="text-red-600"> · {conn.last_error}</span> : ""}</p>
          </div>
          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={sync} disabled={!!busy} className="gap-1" data-testid="quickbooks-sync-btn"><ArrowsClockwise size={13} className={busy === "sync" ? "animate-spin" : ""} /> Pull paid status now</Button>
              <Button size="sm" variant="outline" onClick={test} disabled={!!busy} data-testid="quickbooks-test-btn">Test connection</Button>
              <Button size="sm" variant="ghost" onClick={() => { setOpen(!open); if (!open) loadRef(); }} data-testid="quickbooks-settings-toggle">{open ? "Hide settings" : "Sync settings"}</Button>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={disconnect} disabled={!!busy} data-testid="quickbooks-disconnect-btn">Disconnect</Button>
            </div>
          )}
          {open && (
            <div className="rounded-md border border-border p-3 space-y-3" data-testid="quickbooks-settings">
              {[["push_invoices", "Push invoices when sent"], ["push_payments", "Push payments when paid"], ["push_customers", "Keep customers in sync"], ["pull_status", "Pull paid status every 15 min"]].map(([k, l]) => (
                <label key={k} className="flex items-center justify-between text-xs"><span>{l}</span><Switch checked={settings[k] !== false} onCheckedChange={(v) => save({ [k]: v })} data-testid={`quickbooks-toggle-${k}`} /></label>
              ))}
              {PICKS.map((pk) => (
                <div key={pk.key}>
                  <Label className="text-xs">{pk.label}</Label>
                  <Select value={settings[`${pk.key}_id`] || ""} onValueChange={(v) => { const row = (ref?.[pk.list] || []).find((a) => a.id === v); save({ [`${pk.key}_id`]: v, [`${pk.key}_name`]: row?.name || settings[`${pk.key}_name`] || v }); }}>
                    <SelectTrigger className="mt-1 h-8 text-xs" data-testid={`quickbooks-${pk.key}-select`}><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>{options(pk.list, settings[`${pk.key}_id`], settings[`${pk.key}_name`])}</SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{pk.hint}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <AppNotConfigured item={item} testid="quickbooks-app-missing" />
          {isAdmin ? (
            <Button size="sm" onClick={connect} disabled={!!busy} className="gap-1" data-testid="quickbooks-connect-btn"><Plugs size={14} /> {busy === "connect" ? "Opening Intuit…" : "Connect QuickBooks"}</Button>
          ) : <p className="text-xs text-muted-foreground">Ask an admin to connect QuickBooks.</p>}
          {item?.app?.environment && item?.app?.configured && <p className="text-[10px] text-muted-foreground">App environment: <b className="capitalize">{item.app.environment}</b></p>}
        </div>
      )}
    </CardShell>
  );
}
